import { oc } from '@orpc/contract'
import { z } from 'zod'
import { organizationFeaturesSchema } from './admin'

export const projectSummarySchema = z.object({
  id: z.string().min(1),
  organizationId: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  role: z.enum(['owner', 'admin', 'member']),
  /** Per-project credit cap (≤ the org limit); null = no cap. Owner-editable. */
  creditCap: z.number().nullable(),
})

/**
 * Board m5gc #9 — the owner's instructions for how HUB writes meeting notes.
 * The cap keeps the prompt addition bounded; ~2 000 characters is a solid
 * paragraph of house rules.
 */
export const MEETING_NOTES_INSTRUCTIONS_MAX = 2000

/**
 * The project description rides in every HUB system prompt as "About this
 * project" (board 5q82) — the authority on the project's field, phase,
 * people and priorities. ~2 000 characters ≈ 500 tokens.
 */
export const PROJECT_DESCRIPTION_MAX = 2000

export const projectsGetContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
    })
  )
  .output(
    projectSummarySchema.extend({
      /** The organization's feature flags — what the SPA shows or hides. */
      features: organizationFeaturesSchema,
      /** Appended to every meeting-notes prompt; null = HUB's defaults. Owner/admin-editable. */
      meetingNotesInstructions: z.string().nullable(),
      /** Board 5q82: the whole onboarding was skipped — the chat page stops offering it. */
      onboardingSkippedAt: z.string().nullable(),
    })
  )

export const projectsListForActiveOrgContract = oc
  .input(
    z
      .object({
        organizationId: z.string().min(1).optional(),
      })
      .optional()
  )
  .output(
    z.object({
      items: z.array(projectSummarySchema),
    })
  )

export const projectsCreateContract = oc
  .input(
    z.object({
      organizationId: z.string().min(1),
      name: z.string().trim().min(1).max(120),
      description: z.string().trim().max(PROJECT_DESCRIPTION_MAX).optional(),
      icon: z.string().max(50).nullable().optional(),
    })
  )
  .output(projectSummarySchema)

export const projectsUpdateContract = oc
  .input(
    z
      .object({
        projectId: z.string().min(1),
        name: z.string().trim().min(1).max(120).optional(),
        description: z.string().trim().max(PROJECT_DESCRIPTION_MAX).nullable().optional(),
        icon: z.string().max(50).nullable().optional(),
        /** Project credit cap; null clears it. Only the project owner may set it. */
        creditCap: z.number().int().min(1).nullable().optional(),
        /** Board m5gc #9; null or blank clears it. Owner/admin. */
        meetingNotesInstructions: z
          .string()
          .trim()
          .max(MEETING_NOTES_INSTRUCTIONS_MAX)
          .nullable()
          .optional(),
      })
      .refine(
        (v) =>
          v.name !== undefined ||
          v.description !== undefined ||
          v.icon !== undefined ||
          v.creditCap !== undefined ||
          v.meetingNotesInstructions !== undefined,
        { message: 'At least one field must be provided' }
      )
  )
  .output(projectSummarySchema)

export const projectsDeleteContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      confirmName: z.string().min(1),
    })
  )
  .output(z.object({ ok: z.literal(true) }))

export const projectsTransferContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      targetOrganizationId: z.string().min(1),
    })
  )
  .output(projectSummarySchema)

const projectGroupSchema = z.object({
  organization: z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    role: z.enum(['owner', 'admin', 'member']),
  }),
  projects: z.array(projectSummarySchema),
  projectCount: z.number(),
  projectLimit: z.number(),
})

export const projectsUploadAvatarContract = oc
  .input(
    z.object({
      projectId: z.string().min(1),
      fileName: z.string().min(1),
      mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
      base64Data: z.string().min(1),
    })
  )
  .output(z.object({ avatarUrl: z.string() }))

export const projectsRemoveAvatarContract = oc
  .input(z.object({ projectId: z.string().min(1) }))
  .output(z.object({ ok: z.literal(true) }))

export const projectsListAllForMeContract = oc.input(z.object({}).optional()).output(
  z.object({
    groups: z.array(projectGroupSchema),
  })
)
